import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";

import "./PostJobFormPage.css";

const API_BASE_URL = process.env.REACT_APP_BACKEND_API_URL;

export default function PostJobFormPage() {
  const navigate = useNavigate();

  // ===============================
  // Current user state
  // ===============================
  const [currentUser, setCurrentUser] = useState({ fullname: "" });

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user) {
      setCurrentUser({ fullname: user.fullname });
    }
  }, []);

  // ===============================
  // Form state
  // ===============================
  const [formData, setFormData] = useState({
    title: "",
    company: "",
    location: "",
    type: "",
    deadline: "",
    summary: "",
    description: "",
    responsibilities: "",
    requirements: "",
    applyType: "external",
    applyLink: "",
    postedBy: "", // will be set after currentUser is loaded
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Update postedBy once currentUser is loaded
  useEffect(() => {
    if (currentUser.fullname) {
      setFormData((prev) => ({ ...prev, postedBy: currentUser.fullname }));
    }
  }, [currentUser]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      // Convert responsibilities & requirements to arrays
      const payload = {
        ...formData,
        responsibilities: formData.responsibilities
          .split("\n")
          .map((r) => r.trim())
          .filter(Boolean),
        requirements: formData.requirements
          .split("\n")
          .map((r) => r.trim())
          .filter(Boolean),
      };

      await axios.post(`${API_BASE_URL}/jobs`, payload);

      // ✅ Show confirmation alert, then go back
      alert("Job posted successfully!");
      navigate(-1);
    } catch (err) {
      console.error("Failed to post job:", err.message);
      setError("Failed to post job. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="post-job-page">
      <h1 className="post-job-title">Post a New Job</h1>

      {/* ===============================
          Back Button
         =============================== */}
      <motion.button
        className="back-btn"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => navigate(-1)}
      >
        &larr; Back
      </motion.button>

      <form className="post-job-form" onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Title</label>
          <input type="text" name="title" value={formData.title} onChange={handleChange} required />
        </div>

        <div className="form-group">
          <label>Company</label>
          <input type="text" name="company" value={formData.company} onChange={handleChange} required />
        </div>

        <div className="form-group">
          <label>Location</label>
          <input type="text" name="location" value={formData.location} onChange={handleChange} required />
        </div>

        <div className="form-group">
          <label>Job Type</label>
          <select name="type" value={formData.type} onChange={handleChange} required>
            <option value="">Select job type</option>
            <option value="Full-time Job">Full-time Job</option>
            <option value="Internship">Internship</option>
            <option value="Part-time Job">Part-time Job</option>
            <option value="Contract">Contract</option>
          </select>
        </div>

        <div className="form-group">
          <label>Deadline</label>
          <input type="date" name="deadline" value={formData.deadline} onChange={handleChange} required />
        </div>

        <div className="form-group">
          <label>Summary</label>
          <textarea name="summary" value={formData.summary} onChange={handleChange} rows={3} required />
        </div>

        <div className="form-group">
          <label>Description</label>
          <textarea name="description" value={formData.description} onChange={handleChange} rows={4} required />
        </div>

        <div className="form-group">
          <label>Responsibilities (one per line)</label>
          <textarea name="responsibilities" value={formData.responsibilities} onChange={handleChange} rows={4} required />
        </div>

        <div className="form-group">
          <label>Requirements (one per line)</label>
          <textarea name="requirements" value={formData.requirements} onChange={handleChange} rows={4} required />
        </div>

        <div className="form-group">
          <label>Apply Type</label>
          <select name="applyType" value={formData.applyType} onChange={handleChange} required>
            <option value="external">External</option>
            <option value="internal">Internal</option>
          </select>
        </div>

        {formData.applyType === "external" && (
          <div className="form-group">
            <label>Apply Link</label>
            <input type="url" name="applyLink" value={formData.applyLink} onChange={handleChange} required />
          </div>
        )}

        <div className="form-group">
          <label>Posted By</label>
          <input type="text" name="postedBy" value={formData.postedBy} disabled />
        </div>

        {error && <div className="form-error">{error}</div>}

        <motion.button
          type="submit"
          className="post-job-btn"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          disabled={loading}
        >
          {loading ? "Posting…" : "Post Job"}
        </motion.button>
      </form>
    </div>
  );
}
